import createSelector from 'lib/web/createSelector'
import config from 'constants/config'
import { PAYMENT_OPTIONS } from 'constants/payment'
import { RefundProtectQuoteRequest, RefundProtectQuotePatchRequest } from 'api/insurance'
import { getTotalsWithoutInsurance, getMerchantFeeAmount } from 'checkout/selectors/payment/checkout'
import { checkoutAccommodationGroupingKey, checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { areOccupantsEqual, mergeOccupants } from 'lib/offer/occupancyUtils'
import { getBookingProtectionEnabledCheckoutItems } from '../view/bookingProtection'
import { groupBy, sum } from 'lib/array/arrayUtils'
import { excludeNullOrUndefined } from 'checkout/utils'
import { getBookingProtectionQuoteKey } from 'lib/checkout/bookingProtection/utils'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { adjustForDecimal } from 'lib/maths/mathUtils'
import { getDepositAmount, getServiceFee } from '../payment/deposit'

const getBookingProtectionOccupantsByAccommodationGroup = createSelector(
  getBookingProtectionEnabledCheckoutItems,
  (items) => {
    if (items.length > 0) {
      const itemMap = groupBy(
        items, checkoutAccommodationGroupingKey)

      return Array.from(itemMap.values()).map((itemList) => {
        const occupants = itemList
          .map(item => 'occupancy' in item ? item.occupancy : null)
          .filter(excludeNullOrUndefined)
        return mergeOccupants(occupants)
      })
    }
  },
)

export const areBookingProtectionOccupantsConsistent = createSelector(
  getBookingProtectionOccupantsByAccommodationGroup,
  (occupants): boolean => {
    return occupants ? areOccupantsEqual(...occupants) : true // if no occupants, that's consistent
  },
)

export const getBookingProtectionOccupantsFromCartItems = createSelector(
  getBookingProtectionOccupantsByAccommodationGroup,
  areBookingProtectionOccupantsConsistent,
  (occupants, isConsistent) => {
    if (isConsistent && occupants) {
      return occupants[0]
    }
  },
)

export const getBookingProtectionStartDateFromCart = createSelector(
  checkoutAccommodationOfferView,
  (accommodationViews) => {
    if (!accommodationViews.hasRequiredData) { return null }

    const accommodationDates = accommodationViews.data.map(item => ({
      startDate: item.startDate,
    }))

    const dates = [...accommodationDates]

    if (dates.length) {
      return moment.min(dates.map(date => moment(date.startDate, ISO_DATE_FORMAT)))
    }
  },
)

const getBookingProtectionCoverAmountFromCart = createSelector(
  getTotalsWithoutInsurance,
  (totals): number | undefined => {
    if (totals.hasRequiredData) {
      const {
        paidPrice = 0,
        price = 0,
        surcharge = 0,
        otherFees = {},
      } = totals.data
      return paidPrice + price + surcharge + sum(Object.values(otherFees))
    }
  },
)

const getBookingProtectionCoverUpdateAmountFromCart = createSelector(
  getTotalsWithoutInsurance,
  (totals): number | undefined => {
    if (totals.hasRequiredData) {
      const {
        price = 0,
        surcharge = 0,
        otherFees = {},
      } = totals.data
      return price + surcharge + sum(Object.values(otherFees))
    }
  },
)

export const getBookingProtectionQuoteRequest = createSelector(
  getBookingProtectionOccupantsFromCartItems,
  getBookingProtectionCoverAmountFromCart,
  getBookingProtectionStartDateFromCart,
  (state: App.State) => state.checkout.cart.currencyCode,
  (occupants, coverAmount, startDate, currencyCode): RefundProtectQuoteRequest | null => {
    if (!occupants || !coverAmount || !startDate) return null

    return {
      brand: 'luxuryescapes',
      currencyCode,
      eventTravelDateTime: startDate.format('YYYY-MM-DDTHH:mm:ss.SSSSSSZZ'),
      totalValue: coverAmount,
      numberOfTickets: occupants.adults + (occupants.children ?? 0) + (occupants.infants ?? 0),
    }
  },
)

export const getBookingProtectionQuoteUpdateRequest = createSelector(
  getBookingProtectionCoverUpdateAmountFromCart,
  getBookingProtectionStartDateFromCart,
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => state.checkout.cart.existingOrder?.bookingProtectionItems,
  (coverAmount, startDate, currencyCode, bookingProtectionItems): RefundProtectQuotePatchRequest | null => {
    const item = bookingProtectionItems?.find(item => item.status === 'completed')
    if (!coverAmount || !startDate || !item) return null
    return {
      brand: 'luxuryescapes',
      currencyCode,
      eventTravelDateTime: startDate.format('YYYY-MM-DDTHH:mm:ss.SSSSSSZZ'),
      totalValue: coverAmount + item.coverAmount,
      quoteId: item.quoteId,
      numberOfTickets: item.numberOfTickets,
    }
  },
)

export const getUpdatedQuoteFromBookingProtectionQuotes = createSelector(
  (state: App.State) => state.bookingProtection.quotes,
  getBookingProtectionQuoteUpdateRequest,
  (quotes, quoteRequest): App.BookingProtectionQuote | undefined => {
    if (!quoteRequest || !quotes) return undefined
    return quotes[getBookingProtectionQuoteKey(quoteRequest)]
  },
)

export const getQuoteFromBookingProtectionQuotes = createSelector(
  (state: App.State) => state.bookingProtection.quotes,
  getBookingProtectionQuoteRequest,
  (quotes, quoteRequest): App.BookingProtectionQuote | undefined => {
    if (!quoteRequest || !quotes) return undefined
    return quotes[getBookingProtectionQuoteKey(quoteRequest)]
  },
)

export const getBookingProtectionQuotePrice = createSelector(
  getQuoteFromBookingProtectionQuotes,
  (quote): number => {
    if (!quote) return 0
    return quote.products.reduce((acc, curr) => {
      if (!curr.totalProductPrice) return acc
      return acc + curr.totalProductPrice
    }, 0)
  },
)

export const getUpdatedBookingProtectionQuotePriceDifference = createSelector(
  getUpdatedQuoteFromBookingProtectionQuotes,
  (state: App.State) => state.checkout.cart.existingOrder?.bookingProtectionItems,
  (quote, existingBookingProtectItems): number => {
    if (!quote) return 0
    const oldQuotePrice = existingBookingProtectItems?.find(bookingProtectItem => bookingProtectItem.status === 'completed')?.total || 0
    const newQuotePrice = quote.products.reduce((acc, curr) => {
      if (!curr.totalProductPrice) return acc
      return acc + curr.totalProductPrice
    }, 0)
    const quotePriceDiff = Math.round((newQuotePrice - oldQuotePrice + Number.EPSILON) * 100) / 100
    // For now, we don't want to refund when negative difference
    return quotePriceDiff < 0 ? 0 : quotePriceDiff
  },
)

export const getUpdatedBookingProtectionQuotePrice = createSelector(
  getUpdatedQuoteFromBookingProtectionQuotes,
  (quote): number => {
    if (!quote) return 0
    return quote.products.reduce((acc, curr) => {
      if (!curr.totalProductPrice) return acc
      return acc + curr.totalProductPrice
    }, 0)
  },
)

export const getBookingProtectionQuotePricePerTraveller = createSelector(
  getBookingProtectionQuotePrice,
  (state: App.State) => state.checkout.cart.items.map(item => isInstantBookingLEHotelItem(item) ? item.occupancy : undefined).filter(excludeNullOrUndefined),
  (quote, occupants): number => {
    if (!quote) return 0
    const numTravellers = occupants.reduce((acc, curr) => acc + curr.adults + (curr.children ?? 0) + (curr.infants ?? 0), 0)
    return adjustForDecimal(quote / numTravellers)
  },
)

export const getBookingProtectionDetails = createSelector(
  (state: App.State) => state.checkout.payment.paymentSelected,
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => state.checkout.form.travellerForms,
  getBookingProtectionQuotePrice,
  getBookingProtectionCoverAmountFromCart,
  getBookingProtectionStartDateFromCart,
  getBookingProtectionOccupantsFromCartItems,
  getQuoteFromBookingProtectionQuotes,
  getDepositAmount,
  getServiceFee,
  (state: App.State) => getMerchantFeeAmount(state, state.checkout.payment.paymentSelected),
  (paymentSelected, currencyCode, travellers, quotePrice, coverAmount, startDate, occupants, quote, depositAmount, serviceFee, merchantFee): App.Checkout.BookingProtectionDetails | null => {
    const quoteId = quote?.quoteId
    const eventTravelDateTime = startDate?.format('YYYY-MM-DDTHH:mm:ss.SSSSSSZZ')

    if (!quoteId || !quotePrice || !coverAmount || !eventTravelDateTime) return null

    return {
      quote_id: quoteId,
      total_value: quotePrice,
      order_value: coverAmount,
      brand: config.BRAND,
      is_paid_in_full: paymentSelected === PAYMENT_OPTIONS.FULL,
      initial_payment_value: paymentSelected === PAYMENT_OPTIONS.DEPOSIT ? adjustForDecimal(depositAmount - quotePrice - serviceFee - merchantFee) : undefined,
      currency_code: currencyCode,
      number_of_tickets: (occupants?.adults ?? 0) + (occupants?.children ?? 0) + (occupants?.infants ?? 0),
      event_travel_date_time: eventTravelDateTime,
      traveller_details: travellers.map(
        traveller => ({
          first_name: traveller.firstName,
          last_name: traveller.lastName,
        })),
    }
  },
)

export const getUpdatedBookingProtectionDetails = createSelector(
  getUpdatedBookingProtectionQuotePrice,
  getUpdatedBookingProtectionQuotePriceDifference,
  getBookingProtectionCoverAmountFromCart,
  getUpdatedQuoteFromBookingProtectionQuotes,
  (quotePrice, quotePriceDifference, coverAmount, quote): App.Checkout.UpdatedBookingProtectionDetails | null => {
    const quoteId = quote?.quoteId

    if (!quoteId || !quotePrice || !coverAmount) return null

    return {
      quoteId: quoteId,
      total: quotePrice,
      orderValue: coverAmount,
      brand: config.BRAND,
      quotePriceDiff: quotePriceDifference,
    }
  },
)
